import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { BehaviorSubject, map, Observable, startWith } from 'rxjs';

@Component({
  selector: 'app-selector-autocomplete',
  templateUrl: './selector-autocomplete.component.html',
  styleUrls: ['./selector-autocomplete.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => SelectorAutocompleteComponent),
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

/**
 * @Input {@link data} is either a map that contains key-value relation, the array of strings,
 * or the array of objects, which internal values can be accessed by {@var field}.
 * @Input {@link field} is used to display a concrete field when data is an array of objects {field: string, ...}
 * @Input {@link title} is title above the selector
 * @Input {@link outerClass} any css class to be applied to the outer div wrapper
 * @Input {@link selectedItem} to pre-set selected item that will be displayed
 * @Input {@link props} small properties of the selector
 * @Input {@link isInsideModal} set true if selector is inside of modal
 * @Output {@link valueEmitter} emits new selected value
 * @var {@link isDataReadyForPickerCssStructure} tells if the data has header and subheader, as it is e.x. done in the hornavbar fund selector.
 * See {@link SelectorAutocompletePickerWrapper} for reference.
 */
export class SelectorAutocompleteComponent implements OnChanges {
  @Input() data: Map<any, any> | any[] = new Map<any, any>([]);
  @Input() field = '';
  @Input() title = '';
  @Input() outerClass = 'control-container';
  @Input() selectedItem: string;
  @Input() props = new SelectorProps();
  @Input() isInsideModal = false;
  @Output() valueEmitter = new EventEmitter<string>();
  isSimpleArray = true;
  isDataReadyForPickerCssStructure = false;
  showPopUpModal = false;
  searchInputValue$ = new BehaviorSubject<any>('');

  dataKeys: string[] | any[];
  dataValues: string[];
  filteredData: Observable<any[]>;

  setSelected(value): void {
    this.selectedItem = this.isSimpleArray ? value : value[this.field];
    this.searchInputValue$.next('');
    const valueToEmit = this.dataValues ? (this.data as Map<any, any>).get(value) : this.selectedItem;
    this.valueEmitter.emit(valueToEmit);
    this.onChange(valueToEmit);
    this.showPopUpModal = false;
  }

  setFilteredData(): void {
    this.filteredData = this.searchInputValue$
      .asObservable().pipe(
        startWith(''),
        map((value: string): any[] => this.filter(value)),
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field) {
      this.isSimpleArray = (this.field === '');
    }
    if (changes.data && this.data) {
      if (this.data instanceof Map) {
        this.dataKeys = Array.from(this.data.keys());
        this.dataValues = Array.from(this.data.values());
      } else {
        this.dataKeys = this.data;
      }
      this.checkIsDataReadyForPickerStructure();
      this.setFilteredData();
    }
  }

  /**
   * Write form value to the DOM element (model => view)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  writeValue(value: any): void {
    this.selectedItem = value;
  }

  /**
   * Update form when DOM element value changes (view => model)
   */
  registerOnChange(fn: any): void {
    // Store the provided function as an internal method.
    this.onChange = fn;
  }

  /**
   * Update form when DOM element is blurred (view => model)
   */
  registerOnTouched(fn: any): void {
    // Store the provided function as an internal method.
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange(value: any): void {
  }

  onTouched(): void {
  }

  /**
   * Precondition: {@link dataKeys} must be already initialized.
   * Checks if the user is using {@link SelectorAutocompletePickerWrapper} to provide the data.
   * If yes -> the new view will be shown (see docs).
   */
  private checkIsDataReadyForPickerStructure(): void {
    if (this.dataKeys.length && this.dataKeys[0] instanceof SelectorAutocompletePickerWrapper) {
      this.isDataReadyForPickerCssStructure = true;
    }
  }

  private filter(value: string): any[] {
    const filterValue = value?.toString().toLowerCase();
    return this.dataKeys?.filter((el) => {
      if (this.isDataReadyForPickerCssStructure) {
        const wrapper = el as unknown as SelectorAutocompletePickerWrapper;
        return wrapper.header?.toLowerCase().includes(filterValue) || wrapper.subheader?.toLowerCase().includes(filterValue);
      }
      const property = this.isSimpleArray ? el : el[this.field];
      return property?.toString().toLowerCase().includes(filterValue);
    });
  }
}


/**
 * {@link required} displays a red star near the title when true
 * {@link maxListEntryLength} max number of chars displayed in selector picker
 * {@link openOnClick} set false to open selector on hover
 */
class SelectorProps {
  required = false;
  maxListEntryLength = -1; // not limited by default (when = -1)
  openOnClick = true;
}

/**
 * There are two types of entries: header and subheader.
 * In html, header will be displayed at the top and subheader at the bottom.
 * In html, only subheader can be chosen, header cannot be chosen.
 * In code, header and subheader are separate entries.
 * <p>
 * For header:
 * <pre>
 * id: all subheaders for the header, divided by ','. This is needed for filtering both subheader and header at the same time.
 * header: the header title itself
 * subheader: null
 * isHeader: true
 * </pre>
 * For subheader:
 * <pre>
 * id: unique subheader, which will be checked with 'selectedItem' to add extra `selected` style.
 * header: the header of the subheader.
 * subheader: string
 * isHeader: false
 * </pre>
 *
 */
export class SelectorAutocompletePickerWrapper {
  constructor(
    public id: string,
    public header: string,
    public subheader: string,
    public isHeader: boolean,
    public comment?: string,
  ) {
  }
}
